import { ExternalAppId, ExternalAppStatus } from "../generated/enums"
import { omniaResolve } from "../promises"
import { IOmniaSession } from "../types"
import { createFilesInterface } from "./appFiles"
import { AppFilesService } from "./appFilesService"
import { AppMachinesService } from "./appMachinesService"
import { AppSession } from "./appSession"
import { getSupportedApp, getSupportedApps } from "./definitions"
import { IAppConfig, IAppConnection, IAppDefinition, IAppService, IAppSession, IExportTarget } from "./types"

interface ApiConnection {

    AppId: ExternalAppId
    ConnectionGuid: string

    UserId: number
    UserName: string

    Status: ExternalAppStatus

    HoldingId: number
    Organisation: string

}

/** @internal */
export class AppServiceImpl implements IAppService {


    public readonly files: AppFilesService
    public readonly machines: AppMachinesService

    private loading: ng.IPromise<any>

    private cache: IAppConnection[] = []

    static $inject = ["$q", "$rootScope", "$timeout", "OmniaSession"]

    constructor(
        private readonly $q: ng.IQService,
        private readonly $rootScope: ng.IRootScopeService,
        private readonly $timeout: ng.ITimeoutService,
        public readonly session: IOmniaSession,
        private readonly ohttp = session.http) {

        this.files = new AppFilesService(this.ohttp)
        this.machines = new AppMachinesService(this, this.ohttp)

        // The first call to refresh() loads available connections without revalidating them,
        // so should be relatively quick
        this.refresh().then(() => {

            this.$timeout(2000)
                // This delayed call to loadConnections is there to force a initial revalidation in the background
                .then(() => this.loadConnections(true))
                .then(changed => {
                    if (changed) {
                        this.broadcastChange("revalidate")
                    }
                })

        })

    }

    getSupportedApps(): IAppDefinition[] {
        return getSupportedApps(this.session)
    }

    refresh(revalidate?: boolean) {

        this.cache = []
        return this.loading || this.loadConnections(revalidate)

    }

    broadcastChange(change?: string) {
        this.$rootScope.$broadcast("omnia.apps.change", change)
    }

    createSession(def: IAppDefinition) {
        return new AppSession(def, this)
    }

    createConnection(appId: ExternalAppId) {

        const url = `/api/apps/create?appId=${appId}`

        return this.ohttp.post(url).then(r => {
            const result = r.data
            const cn = this.cacheConnection(appId, result)
            return {
                ...cn,
                authorizeUri: result.OAuthAuthorizeUri
            }
        })

    }

    private checkConnection(connectionId: string): ng.IPromise<boolean> {

        const url = `/api/apps/check?id=${connectionId}`

        return this.ohttp.get(url).then(r => this.setConnectionStatus(connectionId, !!r.data))

    }

    watchNewConnection(connectionId: string, onConnected: Function): Function {

        let timeout: ng.IPromise<any>

        const cancelTimeout = () => {
            if (timeout) {
                this.$timeout.cancel(timeout)
                timeout = undefined
            }
        }

        const watch = () => this.checkConnection(connectionId)
            .then(ok => {

                return ok
                    ? this.loadConnections(false).then(_ => true)
                    : false

            })
            .then(ok => {

                if (ok) {
                    cancelTimeout()
                    onConnected()
                    this.broadcastChange()
                }

            })
            .finally(() => {

                timeout = timeout
                    ? this.$timeout(watch, 5000)
                    : undefined

            })

        timeout = this.$timeout(watch, 5000)

        return cancelTimeout

    }


    private loadConnections(validate: boolean) {

        const current = [...this.cache]

        this.loading = this.loadConnectionsInternal(validate)
            .then(() => checkValidityChanges(current, this.cache))
            .finally(() => this.loading = undefined)

        return this.loading

    }

    private loadConnectionsInternal(validate: boolean) {

        const url = `/api/apps/get/all?validate=${!!validate}`

        const { session } = this

        return this.ohttp.get(url, false)
            .then(r => {

                const connections = r.data as ApiConnection[]

                this.cache = connections.map(a => setupApiConnection(session, a))

            })

    }

    getConnection(appId: ExternalAppId): ng.IPromise<IAppConnection> {

        if (this.loading) {
            return this.loading.then(() => this.getConnection(appId))
        }

        const cn = this.getCachedConnection(appId)

        return this.$q.resolve(cn)

    }

    getConnections(validate?: boolean) {

        if (this.loading) {
            return this.loading.then(() => this.getConnections(validate))
        }

        if (validate) {
            return this.loadConnections(true)
        }

        const connections = [...this.cache]

        return this.$q.resolve(connections)

    }

    checkSyncData(connectionId): ng.IPromise<boolean> {
        const url = `/api/apps/sync/status?id=${connectionId}`
        return this.ohttp.get(url).then(r => !!r.data)
    }

    cancelConnection(connectionId: string): ng.IPromise<any> {
        const url = `/api/apps/cancel?id=${connectionId}`
        return this.ohttp.post(url).then(() => this.removeCachedConnection(connectionId))
    }

    disconnect(connectionId: string, deleteData: boolean): ng.IPromise<any> {
        const url = `/api/apps/disconnect?id=${connectionId}&deleteData=${deleteData}`
        return this.ohttp.post(url).then(() => this.removeCachedConnection(connectionId))
    }

    validateBasicConnection(connectionId: string, user: string, password: string): ng.IPromise<boolean> {
        const url = `/api/apps/validate/basic`
        const data = {
            connectionId,
            user,
            password
        }
        return this.ohttp.post(url, data).then(r => this.setConnectionStatus(connectionId, !!r.data))
    }

    getFiles(app: IAppSession)
    getFiles(app: IAppDefinition)
    getFiles(from: IAppSession | IAppDefinition) {

        if (!from) {
            return undefined
        }

        function isSessionInstance(a: any): a is AppSession {
            return a instanceof AppSession
        }

        if (isSessionInstance(from)) {
            return omniaResolve(createFilesInterface(from, this.files))
        }

        const s = this.createSession(<IAppDefinition>from)

        return s.getConnection().then(() => createFilesInterface(s, this.files))

    }

    getConfig(connectionId: string): ng.IPromise<IAppConfig> {

        const url = `/api/apps/config/${connectionId}`

        return this.ohttp.get(url, false).then(r => r.data)

    }

    saveConfig(connectionId: string, org: { id: string, name: string }, config: any): ng.IPromise<any> {

        const url = "/api/apps/config"

        const data = {
            connectionId,
            org: org?.id,
            orgName: org?.name,
            config
        }

        return this.ohttp.post(url, data).then(() => {

            this.updateCachedConnection(connectionId, cn => {

                let holding = cn.holding

                if (!holding) {
                    return cn
                }

                holding = {
                    ...holding,
                    organisation: org?.id,
                    config
                }

                return {
                    ...cn,
                    invalidConnection: checkInvalidConnection(cn.status, cn.app, holding),
                    holding
                }

            })

        })

    }

    sendFile(target: IExportTarget): ng.IPromise<any> {
        return this.files.sendFile(target)
    }


    private getCachedConnection(appId: ExternalAppId) {
        return this.cache.find(c => c.appId == appId)
    }

    private removeCachedConnection(connectionId: string) {
        this.cache.remove(c => c.connectionId == connectionId)
    }

    private cacheConnection(appId: ExternalAppId, api: ApiConnection) {

        const cacheEntry = this.getCachedConnection(appId)

        if (cacheEntry) {
            this.cache.remove(i => i == cacheEntry)
        }

        if (!api) return null

        const cn = setupApiConnection(this.session, api)

        this.cache.push(cn)

        return cn

    }

    private setConnectionStatus(connectionId: string, connected: boolean) {

        if (connected) {
            this.updateCachedConnection(connectionId, cn => ({ ...cn, status: 0 }))
        }

        return connected

    }

    private updateCachedConnection(connectionId: string, update: (cn: IAppConnection) => IAppConnection) {

        const i = this.cache.findIndex(cn => cn.connectionId == connectionId)

        if (i >= 0) {
            return (this.cache[i] = update(this.cache[i]))
        }

    }

}

function setupApiConnection(session: IOmniaSession, api: ApiConnection) {

    const holding = api.HoldingId
        ? {
            holdingId: api.HoldingId,
            organisation: api.Organisation,
            config: {}
        }
        : undefined

    const app = getSupportedApp(session, api.AppId)

    const cn: IAppConnection = {
        appId: api.AppId,
        app,
        connectionId: api.ConnectionGuid,
        userId: api.UserId,
        userName: api.UserName,
        status: api.Status || 0,
        invalidConnection: checkInvalidConnection(api.Status, app, holding),
        holding
    }

    return cn

}

function checkInvalidConnection(status: ExternalAppStatus, app: IAppDefinition, holding: IAppConnection["holding"]) {

    status = status || 0

    if (status !== 0) {
        return true
    }

    if (holding?.organisation) {
        return false
    }

    if (!(app?.orgs?.required)) {
        return false
    }

    return true

}

/**
 * Compares two arrays of connections for changes on the validity state (invalidConnection)
 * @param before
 * @param after
 * @returns true if changes have been detected, false otherwise
 */
function checkValidityChanges(before: IAppConnection[], after: IAppConnection[]) {

    if (before.length != after.length) {
        return true
    }

    return before.some(b => {
        const a = after.find(a => a.connectionId == b.connectionId)
        if (!a) return true
        return (a.invalidConnection !== b.invalidConnection)
    })

}
